<template>
  <main dir="rtl" class="min-h-screen bg-slate-50 text-slate-900">
    <header class="bg-white border-b border-slate-200 sticky top-0 z-20">
      <div class="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
        <div class="flex items-center gap-3">
          <div class="bg-primary p-2 rounded-lg text-white">
            <span class="material-icons-round">schedule</span>
          </div>
          <h1 class="text-xl font-bold">حالة مزامنة أوقات الصلاة</h1>
        </div>
        <button
          class="px-4 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 transition-colors"
          @click="loadStatus"
        >
          تحديث
        </button>
      </div>
    </header>

    <section class="max-w-6xl mx-auto p-6 space-y-6">
      <div class="bg-white border border-slate-200 rounded-2xl p-4">
        <p class="font-bold mb-3">إعادة مزامنة أوقات الصلاة</p>
        <div class="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div>
            <label class="block text-xs text-slate-500 mb-1">النمط</label>
            <select v-model="resyncMode" class="w-full rounded-lg border-slate-200 bg-slate-50">
              <option value="single">يوم محدد</option>
              <option value="range">مدى أيام</option>
            </select>
          </div>
          <div>
            <label class="block text-xs text-slate-500 mb-1">من تاريخ</label>
            <input v-model="startDate" type="date" class="w-full rounded-lg border-slate-200 bg-slate-50" />
          </div>
          <div>
            <label class="block text-xs text-slate-500 mb-1">إلى تاريخ</label>
            <input
              v-model="endDate"
              type="date"
              class="w-full rounded-lg border-slate-200 bg-slate-50"
              :disabled="resyncMode !== 'range'"
            />
          </div>
          <label class="flex items-center gap-2 text-sm text-slate-600">
            <input v-model="includeTomorrow" type="checkbox" class="rounded border-slate-300" />
            شمل الغد عند اليوم الواحد
          </label>
          <button
            class="px-4 py-2 rounded-lg bg-primary text-white hover:opacity-90 disabled:opacity-60"
            :disabled="resyncLoading || !startDate"
            @click="submitResync"
          >
            {{ resyncLoading ? 'جاري المزامنة...' : 'إعادة المزامنة' }}
          </button>
        </div>
        <p v-if="resyncMessage" class="mt-3 text-sm text-emerald-700">{{ resyncMessage }}</p>
      </div>

      <div v-if="loading" class="bg-white border border-slate-200 rounded-xl p-4 text-center text-slate-600">
        جاري تحميل حالة المزامنة...
      </div>
      <div v-if="errorMessage" class="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
        {{ errorMessage }}
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div class="bg-white border border-slate-200 rounded-xl p-4">
          <p class="text-xs text-slate-500 mb-1">المناطق المتوقعة</p>
          <p class="text-2xl font-bold">{{ summary.expected_regions || 0 }}</p>
        </div>
        <div class="bg-white border border-slate-200 rounded-xl p-4">
          <p class="text-xs text-slate-500 mb-1">آخر مزامنة</p>
          <p class="text-sm font-bold">{{ formatDateTime(summary.last_synced_at) }}</p>
        </div>
        <div class="bg-white border border-slate-200 rounded-xl p-4">
          <p class="text-xs text-slate-500 mb-1">تغطية اليوم الحالي</p>
          <p class="text-2xl font-bold">{{ activeDayStatus.coverage_percent ?? today.coverage_percent ?? 0 }}%</p>
        </div>
      </div>

      <div class="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div class="p-4 border-b border-slate-100 flex items-center justify-between gap-3">
          <p class="font-bold">تفاصيل الأيام</p>
          <div class="flex items-center gap-2">
            <button
              class="px-3 py-1 rounded border border-slate-200 bg-white text-sm disabled:opacity-50"
              :disabled="loading || pagination.page <= 1"
              @click="changePage(pagination.page - 1)"
            >
              السابق
            </button>
            <span class="text-sm text-slate-600">صفحة {{ pagination.page }} من {{ pagination.total_pages }}</span>
            <button
              class="px-3 py-1 rounded border border-slate-200 bg-white text-sm disabled:opacity-50"
              :disabled="loading || pagination.page >= pagination.total_pages"
              @click="changePage(pagination.page + 1)"
            >
              التالي
            </button>
          </div>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead class="bg-slate-50 text-slate-600">
              <tr>
                <th class="px-4 py-3 text-center">اليوم</th>
                <th class="px-4 py-3 text-center">المناطق المغطاة</th>
                <th class="px-4 py-3 text-center">المناطق الناقصة</th>
                <th class="px-4 py-3 text-center">عدد الصفوف</th>
                <th class="px-4 py-3 text-center">النسبة</th>
                <th class="px-4 py-3 text-center">آخر تحديث</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="day in days"
                :key="day.date"
                class="border-t border-slate-100 cursor-pointer"
                :class="selectedDate === day.date ? 'bg-primary/5' : ''"
                @click="selectedDate = day.date"
              >
                <td class="px-4 py-3 text-center">{{ day.date || '-' }}</td>
                <td class="px-4 py-3 text-center">{{ day.regions_covered ?? 0 }}</td>
                <td class="px-4 py-3 text-center">{{ day.regions_missing ?? 0 }}</td>
                <td class="px-4 py-3 text-center">{{ day.rows ?? 0 }}</td>
                <td class="px-4 py-3 text-center">{{ day.coverage_percent ?? 0 }}%</td>
                <td class="px-4 py-3 text-center">{{ formatDateTime(day.last_updated_at) }}</td>
              </tr>
              <tr v-if="!days.length" class="border-t border-slate-100">
                <td colspan="6" class="px-4 py-4 text-center text-slate-500">لا توجد أيام للمزامنة</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div class="p-4 border-b border-slate-100 flex items-center justify-between gap-3">
          <p class="font-bold">تفاصيل المناطق</p>
          <p class="text-sm text-slate-600">اليوم المحدد: {{ selectedDate || '-' }}</p>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead class="bg-slate-50 text-slate-600">
              <tr>
                <th class="px-4 py-3 text-center">المنطقة</th>
                <th class="px-4 py-3 text-center">الحالة</th>
                <th class="px-4 py-3 text-center">المصدر</th>
                <th class="px-4 py-3 text-center">الفجر</th>
                <th class="px-4 py-3 text-center">الظهر</th>
                <th class="px-4 py-3 text-center">العصر</th>
                <th class="px-4 py-3 text-center">المغرب</th>
                <th class="px-4 py-3 text-center">العشاء</th>
                <th class="px-4 py-3 text-center">آخر تحديث</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="region in activeRegionDetails"
                :key="`${selectedDate}-${region.region_id}`"
                class="border-t border-slate-100"
              >
                <td class="px-4 py-3 text-center">{{ regionLabel(region) }}</td>
                <td class="px-4 py-3 text-center">
                  <span
                    class="px-2 py-1 rounded-full text-xs"
                    :class="region.is_synced ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'"
                  >
                    {{ region.is_synced ? 'متزامنة' : 'غير متزامنة' }}
                  </span>
                </td>
                <td class="px-4 py-3 text-center">{{ formatSource(region.source) }}</td>
                <td class="px-4 py-3 text-center">{{ formatPrayerTime(region.fajr_time) }}</td>
                <td class="px-4 py-3 text-center">{{ formatPrayerTime(region.dhuhr_time) }}</td>
                <td class="px-4 py-3 text-center">{{ formatPrayerTime(region.asr_time) }}</td>
                <td class="px-4 py-3 text-center">{{ formatPrayerTime(region.maghrib_time) }}</td>
                <td class="px-4 py-3 text-center">{{ formatPrayerTime(region.isha_time) }}</td>
                <td class="px-4 py-3 text-center">{{ formatDateTime(region.updated_at) }}</td>
              </tr>
              <tr v-if="!activeRegionDetails.length" class="border-t border-slate-100">
                <td colspan="9" class="px-4 py-4 text-center text-slate-500">لا توجد بيانات مناطق متاحة</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import { defineComponent } from 'vue';
import { getIqraPrayerTimesSyncStatus, resyncIqraPrayerTimes } from '@/services/iqraSchedules';

export default defineComponent({
  name: 'IqraPrayerTimesSyncStatus',
  computed: {
    activeRegionDetails() {
      const list = this.activeDayStatus?.regions_details;
      return Array.isArray(list) ? list : [];
    },
    activeDayStatus() {
      return this.days.find((day) => day.date === this.selectedDate) || this.days[0] || {};
    },
  },
  data() {
    return {
      loading: false,
      errorMessage: '',
      summary: { expected_regions: 0, last_synced_at: null },
      today: {},
      tomorrow: {},
      days: [],
      selectedDate: '',
      pagination: {
        page: 1,
        per_page: 7,
        total: 0,
        total_pages: 1,
      },
      resyncMode: 'single',
      startDate: '',
      endDate: '',
      includeTomorrow: true,
      resyncLoading: false,
      resyncMessage: '',
    };
  },
  async mounted() {
    this.startDate = this.todayDateValue();
    this.endDate = this.todayDateValue();
    await this.loadStatus();
  },
  methods: {
    async loadStatus(page = this.pagination.page) {
      this.loading = true;
      this.errorMessage = '';
      try {
        const result = await getIqraPrayerTimesSyncStatus({
          page,
          per_page: this.pagination.per_page,
        });
        this.summary = result?.summary || { expected_regions: 0, last_synced_at: null };
        this.today = result?.today || {};
        this.tomorrow = result?.tomorrow || {};
        this.days = Array.isArray(result?.days) ? result.days : [];
        this.pagination = {
          page: Number(result?.pagination?.page || page || 1),
          per_page: Number(result?.pagination?.per_page || this.pagination.per_page || 7),
          total: Number(result?.pagination?.total || 0),
          total_pages: Number(result?.pagination?.total_pages || 1),
        };
        if (!this.selectedDate || !this.days.some((day) => day.date === this.selectedDate)) {
          this.selectedDate = this.days[0]?.date || '';
        }
      } catch (error) {
        this.errorMessage = error?.message || 'فشل في تحميل حالة مزامنة أوقات الصلاة';
      } finally {
        this.loading = false;
      }
    },
    todayDateValue() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    async submitResync() {
      this.resyncLoading = true;
      this.errorMessage = '';
      this.resyncMessage = '';
      try {
        const payload = {
          start_date: this.startDate,
          include_tomorrow: this.resyncMode === 'single' ? this.includeTomorrow : false,
        };
        if (this.resyncMode === 'range' && this.endDate) {
          payload.end_date = this.endDate;
        }

        const result = await resyncIqraPrayerTimes(payload);
        this.resyncMessage = `تمت إعادة المزامنة بنجاح: ${result?.rows ?? 0} صفوف، ${result?.regions ?? 0} مناطق.`;
        await this.loadStatus();
      } catch (error) {
        this.errorMessage = error?.message || 'فشل في إعادة مزامنة أوقات الصلاة';
      } finally {
        this.resyncLoading = false;
      }
    },
    formatDateTime(value) {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      return date.toLocaleString('ar-SA');
    },
    formatPrayerTime(value) {
      if (!value) return '-';
      try {
        const [hourRaw, minuteRaw] = String(value).split(':');
        const hour = Number(hourRaw);
        const minute = Number(minuteRaw);
        if (Number.isNaN(hour) || Number.isNaN(minute)) return String(value);
        const suffix = hour >= 12 ? 'م' : 'ص';
        const hour12 = ((hour + 11) % 12) + 1;
        return `${hour12}:${String(minute).padStart(2, '0')} ${suffix}`;
      } catch (_error) {
        return String(value);
      }
    },
    formatSource(source) {
      if (!source) return '-';
      const map = {
        api: 'API',
        api_fallback: 'API Fallback',
        iqra_prayer_times: 'قاعدة بيانات اقرأ',
      };
      return map[source] || source;
    },
    regionLabel(region) {
      return region?.region_name_ar || region?.region_name_en || `#${region?.region_id || '-'}`;
    },
    async changePage(page) {
      if (page < 1 || page > this.pagination.total_pages || this.loading) return;
      await this.loadStatus(page);
    },
  },
});
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Noto+Kufi+Arabic:wght@300;400;500;600;700&display=swap');
@import url('https://fonts.googleapis.com/icon?family=Material+Icons+Round');

main {
  font-family: 'Noto Kufi Arabic', sans-serif;
}

.material-icons-round {
  font-size: 20px;
}
</style>

